import { FORMATIONS } from '../data/formations.js';

const DUREE = 90;

/**
 * Les statistiques, calculées depuis TES propres matchs.
 *
 * Aucune source gratuite ne donne les statistiques de la saison en cours — vérifié
 * chez cinq fournisseurs, sur l'API officielle de la LFP et sur Wikipédia. Mais
 * l'application n'en a pas besoin : tu poses le onze, tu notes les buts, les passes,
 * les cartons et les changements. Tout s'en déduit, et le résultat est plus juste
 * qu'un flux acheté, parce que ce sont les matchs que TU as suivis.
 *
 * Les minutes en particulier se calculent exactement : un titulaire joue de la
 * première minute jusqu'à sa sortie ou la fin ; un remplaçant, de son entrée jusqu'à
 * la sienne ou la fin. Aucune approximation.
 */
export function statistiquesParJoueur({ historique = [], journaux = [] } = {}) {
  const out = new Map();

  const fiche = (id) => {
    if (!out.has(id)) {
      out.set(id, {
        matchs: 0, titularisations: 0, entrees: 0, minutes: 0,
        buts: 0, passes: 0, jaunes: 0, rouges: 0,
        creneaux: new Map(),
      });
    }
    return out.get(id);
  };

  const parMatch = new Map(journaux.map((j) => [String(j.matchId ?? j.match_id), j]));

  for (const h of historique) {
    const journal = parMatch.get(String(h.matchId));
    const evenements = journal?.evenements ?? [];
    const changements = evenements.filter((e) => e.type === 'chg');
    const table = FORMATIONS[h.formation];

    // Les titulaires, avec le créneau qu'ils ont réellement occupé : c'est cette
    // répartition qui alimente la barre des postes de la fiche joueur.
    (h.titulaires ?? []).forEach((id, index) => {
      if (!id) return;
      const f = fiche(id);
      f.matchs += 1;
      f.titularisations += 1;
      const sortie = changements.find((c) => c.sortId === id);
      f.minutes += sortie?.minute != null ? Math.max(0, Math.min(DUREE, sortie.minute)) : DUREE;
      const code = table?.[index]?.code;
      if (code) f.creneaux.set(code, (f.creneaux.get(code) ?? 0) + 1);
    });

    for (const c of changements) {
      if (!c.entreId) continue;
      const f = fiche(c.entreId);
      f.matchs += 1;
      f.entrees += 1;
      const minute = Math.max(0, Math.min(DUREE, c.minute ?? 0));
      // Un entrant peut ressortir — rare, mais un blessé remplaçant arrive.
      const ressorti = changements.find((x) => x.sortId === c.entreId && (x.minute ?? 0) > minute);
      f.minutes += Math.max(0, (ressorti?.minute ?? DUREE) - minute);
    }

    for (const e of evenements) {
      if (!e.joueurId) continue;
      const f = fiche(e.joueurId);
      if (e.type === 'but') f.buts += 1;
      else if (e.type === 'passe') f.passes += 1;
      else if (e.type === 'jaune') f.jaunes += 1;
      else if (e.type === 'rouge') f.rouges += 1;
    }
  }

  // Mise en forme finale : la répartition devient une liste triée, en parts.
  const final = new Map();
  for (const [id, f] of out) {
    const total = [...f.creneaux.values()].reduce((a, b) => a + b, 0);
    final.set(id, {
      matchs: f.matchs,
      titularisations: f.titularisations,
      entrees: f.entrees,
      minutes: f.minutes,
      buts: f.buts,
      passes: f.passes,
      jaunes: f.jaunes,
      rouges: f.rouges,
      repartition: total
        ? [...f.creneaux.entries()].sort((a, b) => b[1] - a[1]).map(([code, n]) => ({ code, n, part: n / total }))
        : [],
    });
  }
  return final;
}

/** Les places, avec le joueur réellement présent à cette minute-là. */
export function placesALaMinute(places, evenements, minute) {
  const remplace = new Map();
  for (const c of (evenements ?? []).filter((e) => e.type === 'chg')) {
    if ((c.minute ?? 0) > minute) continue;
    // Une chaîne de remplacements sur le même créneau se suit de proche en proche.
    let cible = c.sortId;
    for (const [avant, apres] of remplace) if (apres === cible) cible = avant;
    if (cible) remplace.set(cible, c.entreId);
  }
  return (places ?? []).map((p) => ({ ...p, joueurId: remplace.get(p.joueurId) ?? p.joueurId }));
}
